import { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Loader2, MousePointerClick, RefreshCw, Send } from 'lucide-react';
import {
  engagementRate,
  formatEngagementWhen,
  loadNewsletterEngagementPanelData,
  type CampaignEngagementStats,
} from '../../lib/emailEngagement';
import {
  type CampaignEngagementEvent,
  type CampaignSendTimestamp,
  type CampaignTrendBucketSelection,
  type CampaignTrendSeries,
  buildCampaignEngagementTrend,
  bucketMinutesForMode,
  pickCampaignBucketMinutes,
} from '../../lib/campaignEngagementTrend';
import CampaignClickTrendChart from './CampaignClickTrendChart';
import ClicksByLinkTable from './ClicksByLinkTable';

interface NewsletterEngagementPanelProps {
  newsletterId: string;
  recipientsSent: number;
  /** Total queued rows — used for sent bar fill vs audience. */
  recipientsTotal?: number;
  /** Planned send — fallback anchor before first sent_at. */
  scheduledFor?: string | null;
  /** Bump when parent refreshes send list. */
  refreshKey?: number;
}

function MetricBar({
  label,
  value,
  sub,
  percent,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  percent: number;
  tone: 'gray' | 'sky' | 'violet';
}) {
  const fillCls = {
    gray: 'bg-gray-500 dark:bg-gray-400',
    sky: 'bg-sky-500',
    violet: 'bg-violet-500',
  }[tone];
  const width = Math.max(0, Math.min(100, percent));

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {label}
        </span>
        <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
          {value}
        </span>
      </div>
      <div
        className="h-2.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden"
        role="meter"
        aria-valuenow={width}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label}: ${value}`}
      >
        <div
          className={`h-full rounded-full transition-all duration-300 ${fillCls}`}
          style={{ width: `${width}%` }}
        />
      </div>
      {sub ? (
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">{sub}</p>
      ) : null}
    </div>
  );
}

export default function NewsletterEngagementPanel({
  newsletterId,
  recipientsSent,
  recipientsTotal,
  scheduledFor = null,
  refreshKey = 0,
}: NewsletterEngagementPanelProps) {
  const [stats, setStats] = useState<CampaignEngagementStats | null>(null);
  const [anchorAt, setAnchorAt] = useState<string | null>(null);
  const [trendSends, setTrendSends] = useState<CampaignSendTimestamp[]>([]);
  const [trendEvents, setTrendEvents] = useState<CampaignEngagementEvent[]>([]);
  const [bucketSelection, setBucketSelection] = useState<CampaignTrendBucketSelection>('auto');
  const [hasLiveTrend, setHasLiveTrend] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const trend = useMemo((): CampaignTrendSeries => {
    if (!anchorAt || !hasLiveTrend) {
      return { points: [], bucketMinutes: 15, bucketMode: '15min' };
    }
    const spanMs = Math.max(0, Date.now() - new Date(anchorAt).getTime());
    const bucketMinutes =
      bucketSelection === 'auto'
        ? pickCampaignBucketMinutes(spanMs)
        : bucketMinutesForMode(bucketSelection);
    return buildCampaignEngagementTrend(anchorAt, trendSends, trendEvents, { bucketMinutes });
  }, [anchorAt, hasLiveTrend, trendSends, trendEvents, bucketSelection]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await loadNewsletterEngagementPanelData(
        newsletterId,
        recipientsSent,
        scheduledFor,
      );
      setStats(next.stats);
      setAnchorAt(next.anchorAt);
      setTrendSends(next.sends);
      setTrendEvents(next.events);
      setHasLiveTrend(next.hasLiveTrend);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load engagement');
      setStats(null);
      setTrendSends([]);
      setTrendEvents([]);
    } finally {
      setLoading(false);
    }
  }, [newsletterId, recipientsSent, scheduledFor]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const sent = stats?.recipientsSent ?? recipientsSent;
  const total = recipientsTotal ?? sent;
  const openRatePct = sent > 0 ? ((stats?.uniqueOpens ?? 0) / sent) * 100 : 0;
  const clickRatePct = sent > 0 ? ((stats?.uniqueClickers ?? 0) / sent) * 100 : 0;
  const sentPct = total > 0 ? (sent / total) * 100 : sent > 0 ? 100 : 0;

  const anchorLabel = useMemo(() => {
    if (!anchorAt) return null;
    return formatEngagementWhen(anchorAt);
  }, [anchorAt]);

  const hasTracking =
    stats != null &&
    (stats.source !== 'none' ||
      stats.uniqueOpens > 0 ||
      stats.uniqueClickers > 0 ||
      stats.totalClicks > 0);

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      <div className="px-4 sm:px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Engagement</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {anchorLabel
              ? `Since first send · ${anchorLabel}`
              : 'Opens and clicks from Postmark tracking on scheduled sends.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          title="Refresh"
          aria-label="Refresh engagement"
          className="inline-flex items-center justify-center p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && !stats ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 px-5 py-8">
          <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
          Loading engagement…
        </div>
      ) : error ? (
        <p className="text-sm text-red-700 dark:text-red-300 px-5 py-6">{error}</p>
      ) : (
        <>
          <div className="flex flex-col xl:flex-row xl:items-stretch">
            <div className="xl:w-80 shrink-0 px-4 sm:px-5 py-4 space-y-4 border-b xl:border-b-0 xl:border-r border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/20">
              <MetricBar
                label="Emails sent"
                value={sent.toLocaleString()}
                sub={
                  total > sent
                    ? `${sentPct.toFixed(0)}% of ${total.toLocaleString()} queued`
                    : 'Delivered scheduled emails'
                }
                percent={sentPct}
                tone="gray"
              />
              <MetricBar
                label="Open rate"
                value={engagementRate(stats?.uniqueOpens ?? 0, sent)}
                sub={`${(stats?.uniqueOpens ?? 0).toLocaleString()} unique opens`}
                percent={openRatePct}
                tone="sky"
              />
              <MetricBar
                label="Click rate"
                value={engagementRate(stats?.uniqueClickers ?? 0, sent)}
                sub={`${(stats?.uniqueClickers ?? 0).toLocaleString()} unique clickers · ${(stats?.totalClicks ?? 0).toLocaleString()} total clicks`}
                percent={clickRatePct}
                tone="violet"
              />
            </div>

            <div className="flex-1 min-w-0 p-3 sm:p-4 flex flex-col min-h-[11rem]">
              {hasLiveTrend && trend.points.length > 0 ? (
                <CampaignClickTrendChart
                  series={trend}
                  bucketSelection={bucketSelection}
                  onBucketSelectionChange={setBucketSelection}
                  className="flex-1 min-h-[12rem]"
                />
              ) : stats?.source === 'rollup' ? (
                <div className="flex-1 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 text-center px-4">
                  Click trend chart is not available after rollup — totals above are frozen from{' '}
                  {stats.rolledUpAt
                    ? formatEngagementWhen(stats.rolledUpAt)
                    : 'the measurement window'}
                  .
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 text-center px-4">
                  {hasTracking
                    ? 'Activity chart appears once sends and engagement events arrive.'
                    : 'No opens or clicks recorded yet. Tracking applies to sends after Postmark open/link tracking was enabled.'}
                </div>
              )}
            </div>
          </div>

          {stats?.source === 'rollup' && stats.rolledUpAt ? (
            <p className="px-4 sm:px-5 pb-3 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700/80 pt-3">
              Frozen metrics (rolled up {formatEngagementWhen(stats.rolledUpAt)}). Raw events were
              archived after the measurement window.
            </p>
          ) : stats?.source === 'live' ? (
            <p className="px-4 sm:px-5 pb-3 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700/80 pt-3">
              Live counts from recent events.
            </p>
          ) : null}

          {stats && stats.linkClicks.length > 0 ? (
            <ClicksByLinkTable
              linkClicks={stats.linkClicks}
              filter={{ newsletterId }}
              canViewClickers={stats.source === 'live'}
            />
          ) : null}
        </>
      )}

      <div className="px-4 sm:px-5 py-2 border-t border-gray-100 dark:border-gray-700/80 flex flex-wrap gap-3 text-[11px] text-gray-400 dark:text-gray-500">
        <span className="inline-flex items-center gap-1">
          <Send className="w-3 h-3" /> Sent
        </span>
        <span className="inline-flex items-center gap-1">
          <Eye className="w-3 h-3" /> Opened
        </span>
        <span className="inline-flex items-center gap-1">
          <MousePointerClick className="w-3 h-3" /> Clicked
        </span>
      </div>
    </section>
  );
}
